import * as Sentry from '@sentry/react';
import { type UseQueryResult, useQueries, useQueryClient } from '@tanstack/react-query';
import type { CollectionBenchmarkRef, CollectionScoring, CollectionScoringProgress } from '@vizit/api';
import type { ListCollectionScoringsQueryVariables } from '@vizit/api';
import { useGetCollectionQuery, useGetCollectionScoringQuery, useListCollectionScoringsQuery } from '@vizit/api';
import { useRequestCollectionScoringMutation, useSelectCollectionScoringMutation } from '@vizit/api';
import { collectionQueryPredicate, getAPIErrorCode } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { BenchmarkOption } from '@vizit/organisms';
import * as React from 'react';
import { refreshBenchmarkCollections } from './refresh-benchmark';
import type { RequestScoringsResult } from './use-benchmark-scorings';
import { collectionScoringStatusRefetchInterval } from './use-collection-scorings.polling';
import { type CollectionUploadRescore, collectionScoringsRefetchInterval } from './use-collection-scorings.polling';

/**
 * The benchmark whose scorings are listed: a custom one by `id` or a Vizit one by `retailer`. A `BenchmarkOption`
 * from the benchmark picker fits as it is.
 */
type CollectionScoringBenchmark = Pick<BenchmarkOption, 'kind' | 'id' | 'retailer'>;

/** The route-side reference for a benchmark, or `null` when the option lacks the field its kind needs. */
function toCollectionBenchmarkRef(benchmark: CollectionScoringBenchmark | null): CollectionBenchmarkRef | null {
  if (!benchmark) {
    return null;
  }
  if (benchmark.kind === 'custom') {
    return benchmark.id ? { benchmark_id: benchmark.id } : null;
  }
  return benchmark.retailer ? { retailer: benchmark.retailer } : null;
}

/**
 * What the status polls add up to: live progress by product category, and which scorings have landed. Defined once
 * so `useQueries` memoizes the result: the Map keeps its identity until a status result changes, and consumers can
 * key effects on it.
 */
function combineStatusQueries(results: UseQueryResult<CollectionScoringProgress>[]) {
  return {
    progress: new Map<string, CollectionScoringProgress>(
      results.flatMap((query) => (query.data ? [[query.data.productCategoryId, query.data]] : [])),
    ),
    landedIds: results
      .filter((query) => query.data && query.data.status !== 'pending')
      .map((query) => query.data?.id)
      .join(','),
  };
}

interface UseCollectionScoringsProps {
  workspaceId?: string;
  collectionId: string;
  /** The benchmark to list against; `null` lists nothing, e.g. before the page has resolved one */
  benchmark: CollectionScoringBenchmark | null;
  /** An upload whose rescore the list waits for; it is re-read until the API requests the scorings again */
  upload?: CollectionUploadRescore | null;
}

/**
 * The product categories a collection is scored under against one benchmark, split by state, plus the two writes:
 * requesting scorings for new categories and recording which one the user picked. Same shape as
 * `useBenchmarkScorings`, so the collection page and the Comparison drive the category picker the same way; the
 * selection recorded here is the collection's, so a pick made in the Comparison shows on the collection too. Each
 * pending scoring is polled on its status route (see the polling module); when one lands, ready or failed, the
 * list, the collection's image lists and the collection itself (its `last_scoring`) are re-read. Failed scorings
 * are kept apart so the picker can show them; their categories can be requested again.
 */
function useCollectionScorings({ workspaceId, collectionId, benchmark, upload = null }: UseCollectionScoringsProps) {
  const queryClient = useQueryClient();
  const benchmarkRef = toCollectionBenchmarkRef(benchmark);
  const benchmarkId = benchmarkRef?.benchmark_id ?? null;
  const retailer = benchmarkRef?.retailer ?? null;
  // Keyed on the identifying fields, so a fresh option object each render does not re-key the list.
  const variables = React.useMemo<ListCollectionScoringsQueryVariables>(
    () => ({ workspace_id: workspaceId ?? '', collection_id: collectionId, benchmark_id: benchmarkId, retailer }),
    [workspaceId, collectionId, benchmarkId, retailer],
  );
  const hasBenchmark = benchmarkId !== null || retailer !== null;
  const isEnabled = !!workspaceId && !!collectionId && hasBenchmark;
  const { data, isLoading, isError, isSuccess } = useListCollectionScoringsQuery(variables, {
    enabled: isEnabled,
    refetchInterval: collectionScoringsRefetchInterval(variables, upload),
  });
  const { mutateAsync: requestScoring } = useRequestCollectionScoringMutation();
  const { mutateAsync: selectScoring } = useSelectCollectionScoringMutation();

  const scorings = React.useMemo(() => data ?? [], [data]);
  const scored = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'ready'), [scorings]);
  const processing = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'pending'), [scorings]);
  const failed = React.useMemo(() => scorings.filter((scoring) => scoring.status === 'failed'), [scorings]);

  // The listing comes most recently selected first; requesting a scoring stamps it too, so a category just added
  // reopens as the selection while it scores.
  const lastSelected = scorings.find((scoring) => scoring.lastSelectedAt) ?? null;

  const refresh = React.useCallback(
    () => queryClient.invalidateQueries({ queryKey: useListCollectionScoringsQuery.getKey(variables) }),
    [queryClient, variables],
  );

  // Selecting or landing a scoring moves the collection's `last_scoring`, so the collection is re-read with it.
  const refreshCollection = React.useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: useGetCollectionQuery.getKey({ workspace_id: workspaceId ?? '', collection_id: collectionId }),
      }),
    [queryClient, workspaceId, collectionId],
  );

  // One status poll per pending scoring; each stops on its own once the scoring lands.
  const { progress, landedIds } = useQueries({
    queries: processing.map((scoring) => {
      const statusVariables = { workspace_id: workspaceId ?? '', collection_id: collectionId, scoring_id: scoring.id };
      return {
        // A scoring requested again keeps its id, and its earlier run's status, cached as landed and fresh for the
        // app's stale time, would answer for it. Keyed on the request time too, each run reads its status fresh.
        queryKey: [...useGetCollectionScoringQuery.getKey(statusVariables), scoring.requestedAt],
        queryFn: useGetCollectionScoringQuery.fetcher(statusVariables),
        enabled: !!workspaceId,
        refetchInterval: collectionScoringStatusRefetchInterval(statusVariables),
      };
    }),
    combine: combineStatusQueries,
  });

  // The paged reads, the grid's infinite list and the asset header's single-image read all carry the pair scores.
  const refreshImages = React.useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: collectionQueryPredicate(
          ['SearchCollectionImages', 'SearchCollectionImagesInfinite', 'GetCollectionImagePairScore'],
          collectionId,
        ),
      }),
    [queryClient, collectionId],
  );

  // A scoring that landed makes the list stale and gives the collection's images their scores.
  React.useEffect(() => {
    if (!landedIds) {
      return;
    }
    if (workspaceId && benchmarkId) {
      // The benchmark's collections listing carries each collection's latest scoring.
      void refreshBenchmarkCollections(queryClient, workspaceId, benchmarkId);
    }
    refresh();
    refreshCollection();
    void refreshImages();
  }, [landedIds, refresh, refreshCollection, refreshImages, queryClient, workspaceId, benchmarkId]);

  /**
   * Scores the collection under each category against the benchmark, all at once; resolves with the scorings the
   * API accepted and the categories it refused, so the caller can retry only those.
   */
  const requestScorings = React.useCallback(
    async (categoryIds: string[]): Promise<RequestScoringsResult<CollectionScoring>> => {
      if (!isEnabled) {
        return { requested: [], failedCategoryIds: categoryIds };
      }
      const settled = await Promise.allSettled(
        categoryIds.map((categoryId) => requestScoring({ ...variables, product_category_id: categoryId })),
      );
      const failureCodes: Record<string, string | undefined> = {};
      const result: RequestScoringsResult<CollectionScoring> = { requested: [], failedCategoryIds: [], failureCodes };
      settled.forEach((outcome, index) => {
        const categoryId = categoryIds[index] as string;
        if (outcome.status === 'fulfilled') {
          result.requested.push(outcome.value);
          return;
        }
        result.failedCategoryIds.push(categoryId);
        failureCodes[categoryId] = getAPIErrorCode(outcome.reason);
        Sentry.captureException(outcome.reason, {
          tags: {
            feature: SENTRY_FEATURE_TAGS.Collection,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionScoringRequest,
          },
          extra: { ...variables, product_category_id: categoryId },
        });
      });
      // Whatever got through is now listed, pending, and counts against a custom benchmark.
      await refresh();
      if (benchmarkId && result.requested.length > 0) {
        void refreshBenchmarkCollections(queryClient, variables.workspace_id, benchmarkId);
      }
      return result;
    },
    [isEnabled, variables, requestScoring, refresh, benchmarkId, queryClient],
  );

  /**
   * Records that the user switched to a category. Best effort: the page already shows it. A scoring just requested
   * is not in the list yet, so a caller holding it passes it along.
   */
  const recordSelection = React.useCallback(
    (categoryId: string, known?: CollectionScoring) => {
      const scoring = known ?? scorings.find((candidate) => candidate.productCategoryId === categoryId);
      if (!isEnabled || !scoring) {
        return;
      }
      const { workspace_id, collection_id } = variables;
      selectScoring({ workspace_id, collection_id, scoring_id: scoring.id })
        // The cached list keeps its `lastSelectedAt` fresh, so a remount reopens on this category.
        .then(() => Promise.all([refresh(), refreshCollection()]))
        .catch((error) => {
          // A benchmark under its minimum is refused for good; the bar already says why, so it is not an error here.
          if (getAPIErrorCode(error) === 'BENCHMARK_TOO_SMALL') {
            return;
          }
          Sentry.captureException(error, {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Collection,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionScoringSelect,
            },
            extra: { workspace_id, collection_id, scoring_id: scoring.id },
          });
        });
    },
    [isEnabled, variables, scorings, selectScoring, refresh, refreshCollection],
  );

  return {
    scorings,
    scored,
    processing,
    failed,
    /** Live progress of each pending scoring, by product category ID */
    progress,
    /** Reads the collection's images again, with their pair scores */
    refreshImages,
    lastSelected,
    // A disabled query reports isLoading false, so a workspace that has not resolved yet counts as loading. Without
    // a benchmark nothing is expected, so nothing is loading.
    isLoading: hasBenchmark && (isLoading || !workspaceId),
    /** The list has been read at least once; image requests that depend on a category wait for this */
    isResolved: isSuccess || isError,
    isError,
    /** Reads the list again, e.g. after it failed */
    retry: refresh,
    requestScorings,
    recordSelection,
  };
}

export { toCollectionBenchmarkRef, useCollectionScorings };
export type { CollectionScoringBenchmark };
